import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";

export default function Loppeting() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get("kategori") || "alle";
  const [statusFilter, setStatusFilter] = useState("alle");

  useEffect(() => {
    // Set document title and meta description
    document.title = "Loppeting - Havneloppen";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Gå på opdagelse i vores store udvalg af loppeting hos Havneloppen i Nyborg. Vi har alt fra retro møbler og design til tøj, porcelæn og keramik."
      );
    }

    // Fetch products from CMS
    fetch("https://api.dev.auxo.dk/public/content/produkter")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setProducts(data);
        }
      })
      .catch((err) => console.error("Error fetching products:", err))
      .finally(() => setLoading(false));
  }, []);

  const categories = [
    { label: "Alle varer", value: "alle" },
    { label: "Billeder/film og lyd", value: "billeder-film-og-lyd" },
    { label: "Børnenes univers", value: "bornenes-univers" },
    { label: "Design", value: "design" },
    { label: "Elektonik", value: "elektonik" },
    { label: "Lamper", value: "lamper" },
    { label: "Læsestof", value: "laesestof" },
    { label: "Malerier", value: "malerier" },
    { label: "Møbler", value: "mobler" },
    { label: "Porcelæn & Keramik", value: "porcelan-og-keramik" },
    { label: "Småting til hjemmet", value: "smating-til-hjemmet" },
  ];

  const statuses = [
    { label: "Alle statusser", value: "alle" },
    { label: "Til salg i butikken", value: "til-salg-i-butikken" },
    { label: "Tilgængelig", value: "tilgaengelig" },
    { label: "Spørg i butikken", value: "sporg-i-butikken" },
    { label: "Solgt", value: "solgt" },
  ];

  // Filter products based on active filters
  const filteredProducts = products.filter((product) => {
    const clean = (str) => (str || "").toLowerCase().trim().replace(/[^a-z0-9æøå]/gi, "-");
    const pCatSlug = clean(product.terms?.kategori?.[0]);
    const pCatText = clean(product.kategori);
    const pStatus = product.terms?.status?.[0] || product.status;

    const matchesCategory =
      categoryFilter === "alle" ||
      pCatSlug === categoryFilter ||
      pCatText.includes(categoryFilter) ||
      categoryFilter.includes(pCatSlug);

    const matchesStatus =
      statusFilter === "alle" ||
      pStatus === statusFilter ||
      product.status === statusFilter;

    return matchesCategory && matchesStatus;
  });

  const handleCategoryChange = (cat) => {
    if (cat === "alle") {
      searchParams.delete("kategori");
    } else {
      searchParams.set("kategori", cat);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="bg-[#fcfcf9] min-h-screen">
      {/* Hero & Breadcrumb Header */}
      <section className="relative bg-brand-blue text-warm-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/735044027-783346678134345-8998957132513432200-n.jpg"
            alt="Unikke loppefund & genbrugsguld"
            className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-orange mb-4">
            <Link to="/" className="hover:text-warm-white transition-colors">FORSIDE</Link>
            <span className="text-stone-400">/</span>
            <span className="text-warm-white">KATEGORIER</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-warm-white sm:text-4xl md:text-5xl">
            Kategorier & unikke loppefund
          </h1>
        </div>
      </section>

      {/* Intro Subtext Section */}
      <section className="py-8 md:py-12 bg-warm-white border-b border-warm-beige">
        <div className="mx-auto max-w-7xl px-6">
          <p className="text-sm md:text-base text-dark-graphite-light leading-relaxed">
            Her kan du se et udvalg af de spændende loppeting, vi har i butikken lige nu. Da vi er en levende, fysisk butik med stor udskiftning, kan varer hurtigt blive solgt. Vi har derfor <strong className="text-brand-blue font-semibold">ingen webshop eller online betaling</strong> – hvis du ser noget, du kan lide, kan du spørge om varen her på siden, ringe til os eller kigge forbi butikken i Nyborg!
          </p>
        </div>
      </section>

      {/* Horizontal Category Filter Bar */}
      <div className="mx-auto max-w-7xl px-6 pt-12 pb-12">
        <div className="flex flex-wrap justify-center gap-3">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => handleCategoryChange(cat.value)}
              className={`px-6 py-2 rounded-full text-xs font-medium uppercase tracking-wider transition-all duration-200 ${
                categoryFilter === cat.value
                  ? "bg-brand-orange text-white"
                  : "bg-[#f5f3ed] text-dark-graphite hover:bg-stone-200/50"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Status Filter Dropdown */}
        <div className="flex justify-center items-center gap-3 mt-6 border-b border-stone-200/60 pb-8">
          <label htmlFor="status-filter" className="text-xs font-bold tracking-wider uppercase text-dark-graphite whitespace-nowrap">
            <i className="fa-solid fa-filter text-brand-blue mr-1.5" />
            Vis kun:
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-full border border-stone-200 bg-white px-4 py-1.5 text-xs text-dark-graphite font-medium focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
          >
            {statuses.map((stat) => (
              <option key={stat.value} value={stat.value}>
                {stat.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Product Catalog Grid */}
      <div className="mx-auto max-w-7xl px-6 pb-20">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange" />
              <span className="text-sm font-medium text-dark-graphite-light">Henter loppefund...</span>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-stone-200 p-12 text-center max-w-md mx-auto bg-[#f5f3ed]/30">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-stone-100 text-stone-400 mx-auto mb-4">
              <i className="fa-solid fa-magnifying-glass text-xl" />
            </div>
            <h3 className="font-display text-lg font-bold text-brand-blue mb-1">Ingen varer fundet</h3>
            <p className="text-sm text-dark-graphite-light">
              Der er desværre ingen varer, der matcher de valgte filtre i øjeblikket. Prøv at nulstille dine filtre.
            </p>
            <button
              onClick={() => {
                handleCategoryChange("alle");
                setStatusFilter("alle");
              }}
              className="mt-4 rounded-full bg-brand-blue px-5 py-2 text-xs font-bold text-white hover:bg-brand-blue-hover transition-colors uppercase tracking-wider"
            >
              Nulstil filtre
            </button>
          </div>
        ) : (
          <div>
            <div className="text-xs text-dark-graphite-light mb-6 font-medium uppercase tracking-wider">
              Viser {filteredProducts.length} {filteredProducts.length === 1 ? "vare" : "varer"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Call-To-Action (CTA) Banner */}
      <div className="mx-auto max-w-7xl px-6 mb-20">
        <div className="bg-brand-blue rounded-xl relative overflow-hidden p-10 md:p-16 text-white">
          {/* Subtle background icon/graphic */}
          <div className="absolute right-0 bottom-0 opacity-10 translate-x-1/4 translate-y-1/4 pointer-events-none">
            <i className="fa-solid fa-store text-[240px]" />
          </div>

          <div className="relative z-10 max-w-2xl">
            <span className="text-brand-orange text-xs font-extrabold tracking-widest uppercase block mb-2">
              SØGER DU NOGET HELT SPECIFIKT?
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
              Lad os hjælpe dig med at finde dit næste genbrugsguld
            </h2>
            <p className="text-stone-200 text-sm md:text-base leading-relaxed mb-8">
              Vi får nye varer ind hver eneste dag, og vores netværk af loppe-jægere er stort. Hvis du leder efter et bestemt møbel, et særligt stel eller noget helt tredje, så tøv ikke med at kontakte os. Vi holder gerne øje for dig!
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="bg-brand-orange hover:bg-brand-orange-hover text-white text-xs uppercase font-extrabold tracking-widest px-8 py-4 rounded transition-all"
              >
                Kontakt os nu
              </Link>
              <a
                href="tel:28767915"
                className="border border-white/30 hover:bg-white/10 text-white text-xs uppercase font-extrabold tracking-widest px-8 py-4 rounded flex items-center gap-2 transition-all"
              >
                <i className="fa-solid fa-phone" />
                Ring til butikken
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
